import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { CONFIRMATION_TYPES, TimesheetFormEntry, TimesheetWorkRow } from '@evorait/pace-core';
import { InstantFieldComponent, SelectFieldComponent, SelectFieldOption } from '@evorait/pace-components';
import { TranslateModule } from '@ngx-translate/core';
import { formatRowDuration } from './row-duration';

/**
 * An entry a confirmation recorded against a notification.
 *
 * Almost nothing the confirmation recorded is edited here: the work code, the customer and the
 * notification are all its own, and a correction to them is made in the confirmation — which the
 * notification number links to, so the way there is where the technician looks for it. The times
 * follow the same rule with one exception: the end of the last settled travel-back leg, because a
 * confirmation is written before the journey home is over and its recorded end is only a guess.
 * Only that end, plus a travel-back leg's kind and its means of transport, can be set on the
 * timesheet, and only on the leg the page unlocks. While the confirmation is still open even those
 * are locked and the row is marked in progress — the legacy UI hid such entries entirely, which
 * left their time looking like a gap the technician was asked to fill again.
 */
@Component({
    selector: 'pace-timesheet-work-row',
    standalone: true,
    imports: [FormField, InstantFieldComponent, RouterLink, SelectFieldComponent, TranslateModule],
    templateUrl: './timesheet-work-row.component.html',
    styleUrl: './timesheet-row.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimesheetWorkRowComponent {
    /** the projected row, for everything the form does not edit */
    public readonly row = input.required<TimesheetWorkRow>();

    /** the entry's field, whose start and end the inputs bind to */
    public readonly entry = input.required<FieldTree<TimesheetFormEntry>>();

    /** description of the row's work code, resolved from the value-help slice */
    public readonly workCodeDescription = input('');

    /** description of the notification type, qmnum, manum */
    public readonly notificationDescription = input('');

    /**
     * Where the notification number leads: the confirmation that recorded this entry, and so the
     * place its times are corrected. Empty once that confirmation has been submitted, or when there
     * is no notification to open — in which case the number is shown as plain text.
     */
    public readonly confirmationLink = input<readonly string[]>([]);

    /**
     * ISO instants the native pickers are limited to; empty for no limit. Taken from the row's
     * neighbour-derived bounds, so an entry cannot be dragged past the ones around it.
     */
    public readonly minInstant = input('');
    public readonly maxInstant = input('');

    /**
     * Whether this leg's kind may be switched between billable and standby.
     *
     * Decided by the page, which knows the config: only an unlocked travel-back leg qualifies, and
     * only where standby travel is enabled at all.
     */
    public readonly canEditTravelType = input(false);

    /** Whether the means of transport may be chosen, rather than only shown. */
    public readonly canEditMeansOfTransport = input(false);

    /** The means of transport as text, for the rows that only display it. */
    public readonly meansOfTransportText = input('');

    /** The two travel-back kinds, when they may be chosen between. */
    public readonly travelTypeOptions = input<readonly SelectFieldOption[]>([]);

    /** The means of transport on offer, when one may be chosen. */
    public readonly meansOfTransportOptions = input<readonly SelectFieldOption[]>([]);

    /**
     * The city of the address this entry carries, for the row's second line.
     *
     * The row knows only the entry's address id (`row().address`), not the address slice, so the
     * page resolves the city and hands it over — the row stays dumb.
     */
    public readonly addressCity = input('');

    /**
     * The span the fields currently hold, as `HH:mm`, for the duration column.
     *
     * Read from the fields like the manual row's, though a confirmation's times are not editable
     * here, so that the two rows cannot come to disagree about what the duration column means.
     */
    protected readonly duration = computed(() =>
        formatRowDuration(this.entry().start().value(), this.entry().end().value()),
    );

    /**
     * Who the work was for and where, on the row's second line.
     *
     * Both come from the confirmation that recorded the entry — the customer as the notification's
     * address names it, the city from the address the entry's stored id resolves to, which the page
     * hands over as {@link addressCity} since the row holds only the id — so a row says whose site
     * the time went to without the technician opening anything. Shown for an open confirmation and a
     * submitted one alike; only the link distinguishes them.
     */
    protected readonly customerAndCity = computed(() =>
        [this.row().customerName, this.addressCity()].filter(Boolean).join(', '),
    );

    /**
     * Whether this row's confirmation was a short confirmation, for the row's own second line.
     *
     * Read off `row().confirmationType` here rather than have the page fold a label into
     * {@link notificationDescription}, so the line break between the two is the template's layout
     * to own, not a `\n` hidden inside a string built from translated content.
     */
    protected readonly isShortConfirmation = computed(() => this.row().confirmationType === CONFIRMATION_TYPES.SHORT);
}
